import { flow, getType } from "mobx-state-tree";
import { addUtmToCode } from "../components/utms/utils/UtmUtils";
import { addParameterSetToCode } from "../components/configs/parametersets/utils/ParameterSetsUtil";

/**
 * Reusable function to fetch and hydrate default settings
 * Can be used by both CreateCodeDomain and BatchFormState
 * 
 * @param {Object} params - Configuration object
 * @param {Object} params.componentState - The state object containing all necessary stores
 * @param {Object} params.query - Query parameters for fetching default settings
 * @param {boolean} params.hasExtendedDataModal - Whether component has extendedDataSettingModalState
 * @param {Function} params.selectExtendedDataSelection - Optional function for extended data selection
 * @param {Object} params.designerConfig - Optional designer configuration options
 * @returns {Promise} - Promise that resolves when hydration is complete
 */
export const fetchAndHydrateDefaultSetting = flow(function* (params) {
  const {
    componentState,
    query,
    hasExtendedDataModal = false,
    selectExtendedDataSelection = null,
    designerConfig = {}
  } = params;

  try {
    // Validate required query parameters
    if (!query.groupUuid) {
      console.warn('fetchAndHydrateDefaultSetting: No group selected, skipping default settings fetch');
      return;
    }
    
    console.log('fetchAndHydrateDefaultSetting: Query parameters:', query);

    // Clear Settings
    componentState.utmModalState?.setNoneOpen();
    componentState.parameterSetModalState?.setNoneOpen();
    
    // Only clear extended data modal if component has it
    if (hasExtendedDataModal && componentState.extendedDataSettingModalState) {
      componentState.extendedDataSettingModalState.clearState();
    }

    // Fetch default settings
    yield componentState.defaultSetting?.fetchModelAndHydrate(query);

    // Handle designer configuration
    if (componentState?.defaultSetting?.designerConfig) {
      componentState?.designerModalState?.setCustomOpen();
      componentState?.designerModalState?.designerViewStore?.qrConfigSelectStore?.selectConfig(componentState?.defaultSetting?.designerConfig);
      componentState?.designerModalState?.designerViewStore?.hydrateEditComponent(
        componentState?.defaultSetting?.designerConfig,
        designerConfig.isDesignerTemplateExperience || false,
        designerConfig.isClone || false,
        designerConfig.hydrateDimensions !== undefined ? designerConfig.hydrateDimensions : false
      );
      console.log('fetchAndHydrateDefaultSetting: Hydrating designer config:', componentState?.defaultSetting?.designerConfig);
      yield componentState?.designerModalState?.applyDesign(componentState, componentState?.defaultSetting?.designerConfig);
    } else {
      componentState?.designerModalState?.setNoneOpen();
      yield componentState?.designerModalState?.applyDesign(componentState);
      componentState?.designerModalState?.designerViewStore?.qrConfigSelectStore?.setSelectedConfig("");

      // Update preview image if available
      if (componentState?.designerModalState?.qrPreviewStore?.updateImage) {
        componentState?.designerModalState?.qrPreviewStore.updateImage(componentState?.designerModalState?.qrPreviewStore.shortUrl);
      }
    }

    // Handle UTM configuration
    if (componentState?.defaultSetting?.utm) {
      console.log('fetchAndHydrateDefaultSetting: Setting UTM default:', componentState.defaultSetting.utm);
      componentState.utmModalState?.setCustomOpen();
      componentState?.utmModalState?.utmSelectState.setSelectedUtm(componentState?.defaultSetting?.utm);
      yield componentState?.utmModalState?.fetchUtmWithID(componentState?.defaultSetting?.utm?.uuid);
    } 

    // Handle parameter sets configuration
    if (componentState?.defaultSetting?.parametersets) {
      componentState.parameterSetModalState?.setCustomOpen();
      componentState?.parameterSetModalState?.parameterSetSelectState?.setSelectedParameterSet(componentState?.defaultSetting?.parametersets);
      yield componentState?.parameterSetModalState?.fetchParameterSetWithID(componentState?.defaultSetting?.parametersets.uuid);
    }

    // Handle extended data configuration (only if component supports it)
    if (hasExtendedDataModal && 
        componentState?.defaultSetting?.extendedDataSet && 
        componentState.extendedDataSettingModalState) {
      componentState?.extendedDataSettingModalState?.extendedDataSetSelectStore?.setSelectedExtendedData(componentState?.defaultSetting?.extendedDataSet);
      componentState.extendedDataSettingModalState?.toggleDropdownVisibility();
      componentState.selectExtendedDataSelection();
      componentState.extendedDataSettingModalState?.buildSchema();
    }

    // Apply UTM and parameter sets to code
    addUtmToCode(componentState);
    addParameterSetToCode(componentState);

  } catch (e) {
    const typeName = componentState && getType(componentState) ? getType(componentState).name : 'Unknown';
    console.error(`${typeName} Error: Failed to hydrateDefaultSetting`, e);
    throw e;
  }
});

/**
 * Helper function to build dimension mapping from dimensionSelectViewState
 * @param {Object} dimensionSelectViewState - The dimension select view state
 * @returns {Object} - Dimension mapping object
 */
export const buildDimensionMapping = (dimensionSelectViewState) => {
  let dimensionMapping = {};
  
  if (dimensionSelectViewState?.dimensionsEnabled && dimensionSelectViewState?.dimensionSelectStates) {
    dimensionMapping = dimensionSelectViewState.dimensionSelectStates.reduce(
      (acc, dimensionSelectStore) => {
        acc[dimensionSelectStore.selectStateUuid] = dimensionSelectStore.selectedDimension?.uuid || "";
        return acc;
      },
      {}
    );
  }
  return dimensionMapping;
};

/**
 * Helper function to build query parameters for default settings
 * @param {Object} params - Parameters object
 * @param {Object} params.groupSelectStore - Group selection store
 * @param {Object} params.codeTypeSelectStore - Code type selection store  
 * @param {Object} params.dimensionSelectViewState - Dimension selection state
 * @returns {Object} - Query parameters object
 */
export const buildDefaultSettingQuery = ({ groupSelectStore, codeTypeSelectStore, dimensionSelectViewState }) => {
  const dimensionMapping = buildDimensionMapping(dimensionSelectViewState);
  
  return {
    groupUuid: groupSelectStore?.selectedGroup?.uuid,
    type: codeTypeSelectStore?.selected?.uuid,
    dimensions: dimensionMapping
  };
};